import { Check, Sparkles, LogIn, Plus, BedDouble, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CtaSection from "@/components/CtaSection";
import { CLEANING_TIERS, CLEANING_ADDONS, MOVE_SCOPE, AIRBNB_TURNOVER } from "@/data/site";

export default function CleaningServices() {
  return (
    <>
      <Seo
        title="Cleaning Services Greensboro NC | Standard, Deep & Move-Out | Authentically Yours"
        description="Standard, deep, maintenance, and move-in/move-out cleaning in Greensboro, NC and surrounding areas. Residential & commercial. Request a free estimate."
        path="/cleaning-services"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Cleaning Services", path: "/cleaning-services" },
        ]}
      />
      <PageHero
        eyebrow="Cleaning Services"
        title="Professional Cleaning for Homes, Businesses & Properties"
        intro="Different properties need different levels of care. Choose the clean that fits — from routine upkeep to a detailed deep clean or a move-ready reset."
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Cleaning Services", path: "/cleaning-services" },
        ]}
      />

      {/* Cleaning levels */}
      <section className="container-x py-20 sm:py-28" aria-labelledby="levels-h">
        <SectionHeading eyebrow="Residential Cleaning" title="Choose Your Level of Clean" as="h2" />
        <div className="mt-14 grid gap-6 lg:grid-cols-3 items-start">
          {CLEANING_TIERS.map((tier, i) => (
            <Reveal key={tier.id} delay={i * 0.08}>
              <article className="h-full card-lift bg-white border border-gold/20 p-8 hover:border-rose/40">
                <h3 className="text-3xl text-ink">{tier.name}</h3>
                <p className="mt-3 text-ink/70 text-[0.95rem] leading-relaxed">{tier.tagline}</p>
                {tier.freq && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {tier.freq.map((f) => (
                      <span key={f} className="eyebrow text-rose border border-rose/30 px-3 py-1">{f}</span>
                    ))}
                  </div>
                )}
                {tier.intro && (
                  <p className="mt-6 eyebrow text-ink/60">{tier.intro}</p>
                )}
                <ul className="mt-5 grid gap-2.5">
                  {tier.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-ink/80 text-[0.95rem]">
                      <Check size={17} strokeWidth={2} className="mt-1 text-rose shrink-0" aria-hidden="true" />
                      {it}
                    </li>
                  ))}
                </ul>
                {tier.footnote && (
                  <p className="mt-6 text-xs text-ink/55 border-t border-gold/20 pt-4 leading-relaxed">{tier.footnote}</p>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* New Client Cleaning Policy */}
      <section className="container-x pb-4 sm:pb-8" aria-labelledby="policy-h">
        <Reveal>
          <div className="relative overflow-hidden border border-rose/25 bg-blush-soft/60 p-8 sm:p-12">
            <div className="absolute inset-y-0 left-0 w-1 bg-rose" aria-hidden="true" />
            <div className="max-w-3xl">
              <span className="inline-flex h-12 w-12 items-center justify-center border border-rose/30 text-rose mb-6" aria-hidden="true">
                <ClipboardCheck size={22} strokeWidth={1.25} />
              </span>
              <p className="eyebrow text-rose">New Client Cleaning Policy</p>
              <h2 id="policy-h" className="mt-3 font-serif text-3xl sm:text-4xl text-ink">
                Every New Home Starts With a Deep Clean
              </h2>
              <div className="mt-6 space-y-4 text-ink/75 text-[0.98rem] leading-relaxed">
                <p>
                  To ensure every home receives the level of care and attention it deserves, new
                  residential clients typically begin with a Deep Cleaning. This allows our team to
                  address existing buildup and detailed areas while establishing a clean foundation
                  for future services.
                </p>
                <p>
                  Homes that are already professionally maintained may qualify to begin with Standard
                  or Recurring Maintenance Cleaning following an initial assessment.
                </p>
                <p>
                  All cleaning services are individually quoted based on the size, condition, and
                  specific needs of the property.
                </p>
              </div>
              <Link to="/free-estimate" className="btn-primary mt-8 inline-flex" data-testid="policy-estimate-btn">
                Request Your Free Estimate
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Move-in / Move-out */}
      <section className="bg-ink text-white" aria-labelledby="move-h">
        <div className="container-x py-20 sm:py-28 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionHeading
              eyebrow="Move-In / Move-Out Cleaning"
              title="A Clean, Move-Ready Reset"
              subtitle="Designed for tenants, homeowners, realtors, property managers, apartment communities and rental properties preparing a space for what comes next."
              as="h2"
              dark
            />
            <p className="mt-8 text-white/60 text-sm leading-relaxed max-w-lg">
              Interior appliances and other detailed services can be selected as
              add-ons or included in a customized estimate.
            </p>
          </div>
          <Reveal>
            <div className="border border-gold/20 p-8">
              <span className="inline-flex h-12 w-12 items-center justify-center border border-blush/40 text-blush mb-6" aria-hidden="true">
                <LogIn size={22} strokeWidth={1.25} />
              </span>
              <p className="eyebrow text-blush mb-4">Potential Scope</p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
                {MOVE_SCOPE.map((m) => (
                  <li key={m} className="flex items-start gap-3 text-white/85 text-[0.95rem]">
                    <Check size={16} strokeWidth={2} className="mt-1 text-blush shrink-0" aria-hidden="true" />
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Short-Term Rental / Airbnb Turnover */}
      <section className="bg-blush-soft/50 border-y border-gold/15" aria-labelledby="airbnb-h">
        <div className="container-x py-20 sm:py-28">
          <SectionHeading
            eyebrow="Short-Term Rental / Airbnb Turnover Cleaning"
            title="Guest-Ready Turnovers Between Every Stay"
            subtitle={AIRBNB_TURNOVER.intro}
            as="h2"
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-2 items-start">
            <Reveal>
              <article className="h-full bg-white border border-gold/20 p-8">
                <span className="inline-flex h-12 w-12 items-center justify-center border border-rose/30 text-rose mb-6" aria-hidden="true">
                  <BedDouble size={22} strokeWidth={1.25} />
                </span>
                <h3 className="text-2xl text-ink">Turnover Cleaning Includes</h3>
                <ul className="mt-5 grid gap-2.5">
                  {AIRBNB_TURNOVER.includes.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-ink/80 text-[0.95rem]">
                      <Check size={17} strokeWidth={2} className="mt-1 text-rose shrink-0" aria-hidden="true" />
                      {it}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
            <Reveal delay={0.08}>
              <article className="h-full bg-white border border-gold/20 p-8">
                <span className="inline-flex h-12 w-12 items-center justify-center border border-gold/40 text-gold mb-6" aria-hidden="true">
                  <Plus size={22} strokeWidth={1.25} />
                </span>
                <h3 className="text-2xl text-ink">Optional Add-Ons</h3>
                <ul className="mt-5 grid gap-2.5">
                  {AIRBNB_TURNOVER.addons.map((a) => (
                    <li key={a} className="flex items-start gap-3 text-ink/80 text-[0.95rem]">
                      <Plus size={15} strokeWidth={2} className="mt-1.5 text-gold shrink-0" aria-hidden="true" />
                      {a}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 pt-5 border-t border-gold/20 text-xs text-ink/55 italic leading-relaxed">
                  {AIRBNB_TURNOVER.addonsNote}
                </p>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="container-x py-20 sm:py-28" aria-labelledby="addons-h">
        <SectionHeading
          eyebrow="Customize Your Clean"
          title="Add-Ons & Extras"
          subtitle="These specialty extras aren't automatically included with every Standard or Deep Clean — add exactly what your property needs."
          as="h2"
        />
        <div className="mt-12 flex flex-wrap gap-3">
          {CLEANING_ADDONS.map((a, i) => (
            <Reveal key={a} delay={(i % 5) * 0.05}>
              <span className="inline-flex items-center gap-2 bg-white border border-gold/25 px-4 py-2.5 text-ink/80 text-[0.95rem] hover:border-rose/50 transition-colors">
                <Plus size={15} strokeWidth={2} className="text-rose" aria-hidden="true" />
                {a}
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaSection
        title="Not Sure Which Clean You Need?"
        text="Tell us about your property and we'll help you determine the right service — no guesswork."
        estimateLabel="Request an Estimate"
      />
    </>
  );
}
